import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence, animate, useMotionValue, type PanInfo } from "motion/react";
import { useVisualViewport } from "@/lib/hooks";
import { getTranslations, type Locale } from "@/lib/i18n/config";

/** Resting height of the mobile bottom sheet, as a fraction of the visual viewport. */
const SHEET_DEFAULT = 0.45;
/** The sheet cannot be dragged smaller than this — header, chat and input stay usable. */
const SHEET_MIN = 0.25;
/** A drag released above this fraction snaps back into full-screen mode. */
const SHEET_FULL_AT = 0.8;
interface AgentPanelAnimationProps {
  isVisible: boolean;
  /**
   * Mobile bottom-sheet mode (MWR-609): the panel takes only the lower part of
   * the visual viewport so the page behind stays visible, and renders a drag
   * handle to resize it freely. No effect on `lg+`.
   */
  isCollapsed?: boolean;
  /** Leaves sheet mode — fired on handle tap and on a drag released above 80%. */
  onExpand?: () => void;
  locale?: Locale;
  children: React.ReactNode;
}

/**
 * Animation wrapper for the full agent panel with spring-based entrance/exit
 */
export const AgentPanelAnimation: React.FC<AgentPanelAnimationProps> = t0 => {
  const $ = _c(26);
  const {
    isVisible,
    isCollapsed: t1,
    onExpand,
    locale: t2,
    children
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const locale = t2 === undefined ? "de" : t2;
  const viewport = useVisualViewport();
  let t3;
  if ($[0] !== locale) {
    t3 = getTranslations(locale);
    $[0] = locale;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    agent: t
  } = t3;
  const sheet = useMotionValue(1);
  const didPanRef = useRef(false);
  let t4;
  let t5;
  if ($[2] !== isCollapsed || $[3] !== sheet) {
    t4 = () => {
      const controls = animate(sheet, isCollapsed ? SHEET_DEFAULT : 1, {
        type: "spring",
        stiffness: 260,
        damping: 30
      });
      return () => controls.stop();
    };
    t5 = [isCollapsed, sheet];
    $[2] = isCollapsed;
    $[3] = sheet;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== sheet || $[7] !== viewport?.height) {
    t6 = (_event, info) => {
      didPanRef.current = true;
      const height = viewport?.height ?? window.innerHeight;
      sheet.set(Math.min(1, Math.max(SHEET_MIN, sheet.get() - info.delta.y / height)));
    };
    $[6] = sheet;
    $[7] = viewport?.height;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handlePan = t6;
  let t7;
  if ($[9] !== onExpand || $[10] !== sheet) {
    t7 = () => {
      if (sheet.get() >= SHEET_FULL_AT) {
        onExpand?.();
      }
      window.setTimeout(() => {
        didPanRef.current = false;
      }, 0);
    };
    $[9] = onExpand;
    $[10] = sheet;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handlePanEnd = t7;
  let t8;
  if ($[12] !== onExpand) {
    t8 = () => {
      if (didPanRef.current) {
        return;
      }
      onExpand?.();
    };
    $[12] = onExpand;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleTap = t8;
  let t9;
  if ($[14] !== children || $[15] !== handlePan || $[16] !== handlePanEnd || $[17] !== handleTap || $[18] !== isCollapsed || $[19] !== isVisible || $[20] !== sheet || $[21] !== t || $[22] !== viewport) {
    t9 = isVisible && <motion.div initial={{
      y: "100%",
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: "100%",
      scale: 0.8,
      opacity: 0
    }} transition={{
      type: "spring",
      stiffness: 128,
      damping: 20,
      mass: 1
    }} className={`fixed right-0 top-[var(--agent-top)] h-[var(--agent-height)] max-lg:top-[calc(var(--agent-top)_+_var(--agent-height)*(1_-_var(--agent-sheet)))] max-lg:h-[calc(var(--agent-height)*var(--agent-sheet))] flex flex-col backdrop-blur-sm justify-between w-full lg:w-80 xl:w-96 text-white bg-linear-to-br from-transparent from-50% via-purple/50 via-100% to-purple font-medium z-51 md:mask-[linear-gradient(to_right,transparent_0,black_0.75rem)] ${isCollapsed ? "max-lg:rounded-t-2xl" : ""}`} role="dialog" aria-modal={!isCollapsed} aria-labelledby="agent-panel-title" style={{
      "--agent-top": viewport ? `${viewport.offsetTop}px` : "0px",
      "--agent-height": viewport ? `${viewport.height}px` : "100dvh",
      "--agent-sheet": sheet
    } as React.CSSProperties}>{isCollapsed && <motion.button type="button" onPan={handlePan} onPanEnd={handlePanEnd} onClick={handleTap} className="lg:hidden flex shrink-0 justify-center pt-2 pb-1 cursor-grab touch-none" aria-label={t.expandAssistant}><span className="h-1 w-10 rounded-full bg-white/60" aria-hidden="true" /></motion.button>}{children}</motion.div>;
    $[14] = children;
    $[15] = handlePan;
    $[16] = handlePanEnd;
    $[17] = handleTap;
    $[18] = isCollapsed;
    $[19] = isVisible;
    $[20] = sheet;
    $[21] = t;
    $[22] = viewport;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <AnimatePresence>{t9}</AnimatePresence>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};